import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/og";
import { useNearViewport } from "@/hooks/useNearViewport";
import { heroSrc } from "@/lib/hero-images";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  getPostBySlug,
  loadPosts,
  relatedPosts,
  adjacentPosts,
  readingMinutes,
  formatDate,
  CATEGORY_LABELS,
  CATEGORY_ACCENT,
  type Post,
} from "@/lib/posts";
import { Reveal } from "@/components/site/Reveal";
import { authorSchema, resolveAuthor } from "@/lib/authors";
import { ArrowLeft, ArrowRight, Clock, ArrowUpRight, Quote, Rss, BookOpen, ExternalLink, Sparkles } from "lucide-react";
import { SaveButton } from "@/components/site/SaveButton";
import { ReadToggleButton } from "@/components/site/ReadToggleButton";
import { useReadLog } from "@/lib/read-log";
import { ShareMenu } from "@/components/site/ShareMenu";
import { NewsFeedFooter } from "@/components/site/NewsFeedFooter";
import { FreshnessBadge } from "@/components/site/FreshnessBadge";
import { ArticlePerfOverlay, useRenderCount } from "@/components/site/ArticlePerfOverlay";
import { ArticleImage } from "@/components/site/ArticleImage";
import { ResumeReadingPill } from "@/components/site/ResumeReadingPill";
import { ResetProgressButton } from "@/components/site/ResetProgressButton";
import { getProgress, scrollToRatio, fingerprintContent, useResetProgressOnContentChange } from "@/lib/read-progress";
import { TableOfContents, MobileTableOfContents, slugifyHeading, type TocItem } from "@/components/site/TableOfContents";
import { ArticleProgress } from "@/components/site/ArticleProgress";
import { ArticleEngagement } from "@/components/site/ArticleEngagement";


import { getRequestOrigin } from "@/lib/origin.functions";

function toAbsolute(origin: string, src: string) {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  return `${origin}${src.startsWith("/") ? "" : "/"}${src}`;
}

// Plocka ut en kort, kraftfull mening ur brödtexten för pull-quote.
function pickPullQuote(body: string[]): string | null {
  const candidates: string[] = [];
  for (const para of body.slice(1, 4)) {
    for (const sentence of para.split(/(?<=[.!?])\s+/)) {
      const t = sentence.trim();
      if (t.length >= 60 && t.length <= 180) candidates.push(t);
    }
  }
  return candidates[0] ?? null;
}

export const Route = createFileRoute("/nyheter/$slug")({
  loader: async ({ params }) => {
    const post = await getPostBySlug(params.slug);
    if (!post) throw notFound();
    const [related, neighbors, allPosts] = await Promise.all([
      relatedPosts(post.slug, 4),
      adjacentPosts(post.slug),
      loadPosts(),
    ]);
    const totalPosts = allPosts.length;
    let origin = "";
    try {
      origin = await getRequestOrigin();
    } catch {
      origin = "";
    }
    return { post, origin, related, neighbors, totalPosts };
  },
  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    if (!post) {
      return { meta: [{ title: "Inlägg hittades inte – Skolvyn" }] };
    }
    const origin = loaderData?.origin ?? "";
    const url = absoluteUrl(`/nyheter/${params.slug}`);
    const image = absoluteUrl(post.hero.src);
    const seoTitle = post.seoTitle ?? post.title;
    const seoDescription = post.seoDescription ?? post.dek;

    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: post.title,
          description: seoDescription,
          image: image || undefined,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt ?? post.publishedAt,
          author: authorSchema(resolveAuthor(post.author)),
          publisher: {
            "@type": "Organization",
            name: "Skolvyn",
            url: "https://skolvyn.se",
          },
          isAccessibleForFree: true,
          mainEntityOfPage: url,
          url,
          articleSection: CATEGORY_LABELS[post.category],
          inLanguage: "sv-SE",
          keywords: post.tags?.length ? post.tags.join(", ") : undefined,
          wordCount: (() => {
            const text = [
              ...post.body,
              ...(post.sections ?? []).flatMap((s) => [
                s.heading,
                ...(s.paragraphs ?? []),
                ...(s.list ?? []),
              ]),
            ].join(" ");
            return text.trim().split(/\s+/).filter(Boolean).length;
          })(),
          timeRequired: `PT${Math.max(1, readingMinutes(post))}M`,
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", ".article-dek"],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Skolvyn", item: "/" },
            { "@type": "ListItem", position: 2, name: "Nyheter", item: "/nyheter" },
            { "@type": "ListItem", position: 3, name: CATEGORY_LABELS[post.category], item: `/nyheter?kategori=${post.category}` },
            { "@type": "ListItem", position: 4, name: post.title, item: `/nyheter/${params.slug}` },
          ],
        }),
      },
    ];
    if (post.faq && post.faq.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }

    return {
      meta: [
        { title: `${seoTitle} – Skolvyn` },
        { name: "description", content: seoDescription },
        {
          name: "robots",
          content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
        },
        { name: "author", content: resolveAuthor(post.author).name },
        { property: "og:title", content: seoTitle },
        { property: "og:description", content: seoDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Skolvyn" },
        { property: "og:locale", content: "sv_SE" },
        ...(image
          ? [
              { property: "og:image", content: image },
              { property: "og:image:alt", content: post.heroAlt },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "630" },
            ]
          : []),
        { property: "article:published_time", content: post.publishedAt },
        ...(post.updatedAt ? [{ property: "article:modified_time", content: post.updatedAt }] : []),
        { property: "article:section", content: CATEGORY_LABELS[post.category] },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: seoTitle },
        { name: "twitter:description", content: seoDescription },
        ...(image
          ? [
              { name: "twitter:image", content: image },
              { name: "twitter:image:alt", content: post.heroAlt },
            ]
          : []),
      ],
      links: [
        { rel: "canonical", href: url },
        // LCP-preload för hero-bilden. `imagesrcset`/`imagesizes` gör att
        // browsern kan välja rätt variant redan innan HTML:en har parsats.
        ...(post.hero.avif
          ? [{
              rel: "preload",
              as: "image",
              type: "image/avif",
              href: post.hero.src,
              imageSrcSet: post.hero.avif,
              imageSizes: "(min-width: 1024px) 1024px, 100vw",
              fetchPriority: "high",
            } as unknown as { rel: string; href: string }]
          : []),
        ...(post.hero.webp && !post.hero.avif
          ? [{
              rel: "preload",
              as: "image",
              type: "image/webp",
              href: post.hero.src,
              imageSrcSet: post.hero.webp,
              imageSizes: "(min-width: 1024px) 1024px, 100vw",
              fetchPriority: "high",
            } as unknown as { rel: string; href: string }]
          : []),
      ],
      scripts,
    };
  },
  notFoundComponent: NotFoundView,
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-5 py-section text-center">
      <h1 className="font-display text-h1 text-ink">Något gick fel</h1>
      <p className="mt-3 text-ink-muted">{error instanceof Error ? error.message : "Okänt fel"}</p>
      <Link to="/nyheter" className="mt-6 inline-block text-primary underline">
        Tillbaka till nyheter
      </Link>
    </div>
  ),
  component: NyheterDetail,
});

function NotFoundView() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-section text-center">
      <h1 className="font-display text-h1 text-ink">Inlägget kunde inte hittas</h1>
      <p className="mt-3 text-ink-muted">
        Länken kan vara felaktig eller så har inlägget flyttats.
      </p>
      <Link
        to="/nyheter"
        className="mt-6 inline-flex items-center gap-2 rounded-card bg-primary px-4 py-2.5 text-small font-semibold text-primary-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Alla nyheter
      </Link>
    </div>
  );
}

function ReadingProgress() {
  const ref = useRef<HTMLDivElement | null>(null);
  const liveRef = useRef<HTMLSpanElement | null>(null);
  const pctRef = useRef<HTMLSpanElement | null>(null);
  const pillRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Prestandamål:
    // 1. Aldrig trigga layout/reflow på andra komponenter — vi animerar
    //    `transform: scaleX(...)` (kompositor-endast), inte `width` (layout).
    // 2. Alltid 60fps — vi rAF-loopar bara när ett värde faktiskt ändrats
    //    (target != current) och lerp:ar mot target för mjuk uppdatering
    //    även vid ryckig scroll (t.ex. iOS momentum-fling).
    // 3. Reagera på layoutförändringar utan scroll — ResizeObserver på
    //    <body> räknar om totalhöjd när bilder laddar, embeds mount:as
    //    eller viewporten ändras.
    // 4. Prefers-reduced-motion → helt avstängd (ingen loop, dölj baren).
    //
    // Tillgänglighet:
    // - Baren är en riktig `role="progressbar"` med aria-valuenow och
    //   uppdateras löpande (heltalsprocent) så skärmläsare som pollar
    //   progressbars kan läsa aktuellt värde vid begäran.
    // - En separat sr-only `aria-live="polite"` region annonserar vid
    //   milstolpar (25/50/75/100 %) så uppläsningen inte spammar men
    //   ändå bekräftar att läsprogressen ändras.
    const reduceMq =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;

    let attached = false;
    let target = 0; // 0..1
    let current = 0; // 0..1
    let raf = 0;
    let cachedTotal = 0;
    let lastAriaPct = -1;
    let lastMilestone = 0; // 0/25/50/75/100

    const recomputeTotal = () => {
      const h = document.documentElement;
      cachedTotal = Math.max(0, h.scrollHeight - h.clientHeight);
    };

    const readTarget = () => {
      const h = document.documentElement;
      target = cachedTotal > 0 ? Math.min(1, Math.max(0, h.scrollTop / cachedTotal)) : 0;
      ensureLoop();
    };

    const announce = (pct: number) => {
      const milestones = [25, 50, 75, 100];
      const reached = milestones.filter((m) => pct >= m).pop() ?? 0;
      if (reached && reached !== lastMilestone) {
        lastMilestone = reached;
        const el = liveRef.current;
        if (el) {
          el.textContent =
            reached === 100
              ? "Artikeln utläst."
              : `Läst ${reached} procent av artikeln.`;
        }
      } else if (reached === 0 && lastMilestone !== 0) {
        // Läsaren skrollade tillbaka till toppen – nollställ så samma
        // milstolpe kan annonseras igen nästa gång den passeras.
        lastMilestone = 0;
      }
    };

    const paint = () => {
      const el = ref.current;
      if (!el) return;
      // scaleX från vänster → transform-origin: 0 50%
      el.style.transform = `scaleX(${current})`;
      const pct = Math.round(current * 100);
      if (pct !== lastAriaPct) {
        lastAriaPct = pct;
        el.setAttribute("aria-valuenow", String(pct));
        el.setAttribute("aria-valuetext", `${pct} procent läst`);
        const pctEl = pctRef.current;
        if (pctEl) pctEl.textContent = `${pct}%`;
        const pill = pillRef.current;
        if (pill) {
          // Diskret: dölj i toppen, tona in efter första skroll
          pill.style.opacity = pct >= 1 ? "1" : "0";
        }
        announce(pct);
      }
    };


    const tick = () => {
      raf = 0;
      const diff = target - current;
      // Snappa när vi är tillräckligt nära, annars lerp med ~20% per frame
      // för mjuk catch-up utan att kännas trög.
      if (Math.abs(diff) < 0.0005) {
        current = target;
        paint();
        return; // loop stannar tills nästa scroll/resize
      }
      current += diff * 0.2;
      paint();
      raf = requestAnimationFrame(tick);
    };

    const ensureLoop = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };

    let scrollScheduled = false;
    const onScroll = () => {
      if (scrollScheduled) return;
      scrollScheduled = true;
      // Läs scrollTop på nästa frame → aldrig sync-layout under scroll-callback
      requestAnimationFrame(() => {
        scrollScheduled = false;
        readTarget();
      });
    };

    let ro: ResizeObserver | null = null;
    const enable = () => {
      if (attached) return;
      attached = true;
      const el = ref.current;
      if (el) {
        el.style.transformOrigin = "0 50%";
        el.style.willChange = "transform";
      }
      recomputeTotal();
      readTarget();
      current = target;
      paint();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", () => {
        recomputeTotal();
        readTarget();
      }, { passive: true });
      if ("ResizeObserver" in window) {
        ro = new ResizeObserver(() => {
          recomputeTotal();
          readTarget();
        });
        ro.observe(document.body);
      }
    };
    const disable = () => {
      if (!attached) return;
      attached = false;
      window.removeEventListener("scroll", onScroll);
      ro?.disconnect();
      ro = null;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      const el = ref.current;
      if (el) {
        el.style.transform = "scaleX(0)";
        el.style.willChange = "";
      }
    };

    const apply = () => {
      if (reduceMq?.matches) disable();
      else enable();
    };
    apply();
    reduceMq?.addEventListener?.("change", apply);
    return () => {
      reduceMq?.removeEventListener?.("change", apply);
      disable();
    };
  }, []);
  return (
    <>
      {/*
        Isoleringslager: fixed strip som äger sin egen stacking context och
        CSS containment. Detta garanterar att transform/repaint på baren
        aldrig invaliderar layout, style eller paint för andra komponenter
        på sidan – webbläsaren behandlar strippen som en fristående ö.
        - `isolation: isolate` → egen stacking context oberoende av z-index-krig.
        - `contain: strict` (layout + paint + style + size) → skärmar av
          allt inuti från resten av rendertreet.
        - `translateZ(0)` → egen compositor layer, transform-uppdateringar
          hoppar över main thread layout helt.
        - `pointer-events: none` → fångar aldrig klick från innehållet.
        - `aria-hidden` på wrappern; själva `progressbar`-rollen sitter på
          det inre elementet där aria-valuenow uppdateras.
      */}
      <div
        aria-hidden
        className="pointer-events-none fixed left-0 top-[73px] z-40 h-0.5 w-full motion-reduce:hidden"
        style={{
          isolation: "isolate",
          contain: "strict",
          transform: "translateZ(0)",
          willChange: "transform",
        }}
      >
        <div
          ref={ref}
          role="progressbar"
          aria-label="Läsprogress för artikeln"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={0}
          aria-valuetext="0 procent läst"
          className="absolute inset-0 bg-primary"
          style={{
            transform: "scaleX(0)",
            transformOrigin: "0 50%",
            contain: "layout paint style",
          }}
        />
      </div>
      {/* Milstolpsannonsering för skärmläsare – uppdateras vid 25/50/75/100 %. */}
      <span
        ref={liveRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
      {/*
        Diskret läsprocent-pill i nedre högra hörnet. Delar samma rAF-loop
        som baren (uppdateras via pctRef i paint()) och tonas in efter att
        läsaren skrollat minst 1 %, så den aldrig stör hero-vyn.
        Egen stacking context + contain för att aldrig påverka layout.
      */}
      <div
        ref={pillRef}
        aria-hidden
        className="ovanfor-samtycke pointer-events-none fixed bottom-4 right-4 z-40 rounded-pill border border-border bg-surface/90 px-2.5 py-1 text-small font-medium tabular-nums text-ink-muted shadow-1 backdrop-blur-sm transition-opacity duration-200 motion-reduce:hidden"
        style={{
          opacity: 0,
          isolation: "isolate",
          contain: "layout paint style",
          transform: "translateZ(0)",
        }}
      >
        <span ref={pctRef}>0%</span>
      </div>
    </>

  );
}




function NyheterDetail() {
  const { post, origin, related, neighbors, totalPosts } = Route.useLoaderData() as {
    post: Post;
    origin: string;
    related: Post[];
    neighbors: { prev?: Post; next?: Post };
    totalPosts: number;
  };
  const renderCount = useRenderCount();
  // Prefetcha "Läs vidare"-listan när sektionen närmar sig viewporten.
  // Vi varmkör både route-datat (via TanStack Router) och hero-bilderna
  // (via `<link rel="prefetch" as="image">`) så första klicket öppnas direkt.
  const router = useRouter();
  const relatedSentinelRef = useRef<HTMLDivElement | null>(null);
  useNearViewport(relatedSentinelRef, () => {
    if (typeof document === "undefined") return;
    for (const r of related) {
      // Route + loader-data
      void router.preloadRoute({
        to: "/nyheter/$slug",
        params: { slug: r.slug },
      });
      // Hero-bild (fallback-src räcker för att fylla HTTP-cachen inför navigering)
      const href = heroSrc(r.hero);
      if (!href) continue;
      if (document.head.querySelector(`link[rel="prefetch"][href="${href}"]`)) continue;
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "image";
      link.href = href;
      document.head.appendChild(link);
    }
  });
  const shareUrl = `${origin ?? ""}/nyheter/${post.slug}`;
  const pullQuote = pickPullQuote(post.body);
  // Splitpunkt: vi renderar body i två block och skjuter in pull-quote mellan.
  const splitAt = Math.min(2, Math.max(1, post.body.length - 1));
  const bodyHead = post.body.slice(0, splitAt);
  const bodyTail = post.body.slice(splitAt);

  // Auto-markera som läst efter en kort läspaus (2.5s) så snabba tillbaka-tryck
  // inte råkar markera. Endast första gången – toggle-knappen styr resten.
  const { mark, has: isRead } = useReadLog();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isRead(post.slug)) return;
    const t = window.setTimeout(() => mark(post.slug), 2500);
    return () => window.clearTimeout(t);
  }, [post.slug, mark, isRead]);

  // Fingeravtryck av artikelns läsbara innehåll. Om det ändras jämfört med
  // vad som var aktivt när progressen sparades rensar vi progressen så att
  // läsaren aldrig skickas till en position som inte längre matchar texten.
  const contentFingerprint = useMemo(
    () =>
      fingerprintContent([
        post.title,
        post.dek,
        ...post.body,
        ...(post.sections ?? []).flatMap((s) => [s.heading, ...(s.paragraphs ?? []), ...(s.list ?? [])]),
      ]),
    [post],
  );
  const wasReset = useResetProgressOnContentChange(post.slug, contentFingerprint);

  // Bygg innehållsförteckning från artikelns H2-sektioner (stabila slug-id).
  const tocItems: TocItem[] = useMemo(
    () =>
      (post.sections ?? []).map((s, i) => ({
        id: slugifyHeading(s.heading, i),
        heading: s.heading,
      })),
    [post.sections],
  );


  // Auto-fortsätt där läsaren slutade när navigering skedde via #fortsatt
  // (t.ex. från ett artikelkort i /nyheter som visade läsprogress).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#fortsatt") return;
    if (wasReset) {
      // Innehållet har ändrats – hoppa inte till en gammal position.
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", url);
      return;
    }
    const saved = getProgress(post.slug);
    if (!saved || saved.ratio < 0.05 || saved.ratio > 0.98) return;
    const t = window.setTimeout(() => {
      scrollToRatio(saved.ratio);
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", url);
    }, 320);
    return () => window.clearTimeout(t);
  }, [post.slug, wasReset]);


  // NewsArticle-schemat sätts i head() – ingen dubblett här.


  return (
    <div className="min-h-dvh bg-background">
      {/* Läsprogress */}
      <ReadingProgress />
      <ArticleProgress targetSelector="#article-body" hideBar />
      <ResumeReadingPill slug={post.slug} fingerprint={contentFingerprint} />
      {wasReset ? (
        <div role="status" aria-live="polite" className="sr-only">
          Sparad läsprogress återställdes eftersom artikeln har uppdaterats.
        </div>
      ) : null}
      <ArticlePerfOverlay renderCount={renderCount} />





      {/* Titel-header: split 8/4 (titel + dek) | (författare + share) */}
      <header className="warmth-bg border-b border-border">
        <div className="mx-auto max-w-6xl px-5 pt-10 md:pt-14 pb-10 md:pb-14">
          <Link
            to="/nyheter"
            className="mono inline-flex items-center gap-1.5 text-small uppercase tracking-widest text-ink-muted hover:text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Nyheter & perspektiv
          </Link>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <div className="mono flex flex-wrap items-center gap-3 text-small uppercase tracking-widest text-ink-muted">
                <span className={`rounded-pill px-3 py-1 ${CATEGORY_ACCENT[post.category]}`}>
                  {CATEGORY_LABELS[post.category]}
                </span>
                {post.featured && (
                  <span className="inline-flex items-center gap-1.5 rounded-pill bg-primary/95 px-3 py-1 text-primary-foreground">
                    <span className="relative flex h-2 w-2" aria-hidden>
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-pill bg-primary-foreground opacity-70" />
                      <span className="relative inline-flex h-2 w-2 rounded-pill bg-primary-foreground" />
                    </span>
                    Aktuellt just nu
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> {readingMinutes(post)} min läsning
                </span>
                <span aria-hidden>·</span>
                <span>{formatDate(post.publishedAt)}</span>
                <span aria-hidden>·</span>
                <FreshnessBadge post={post} />
              </div>


              <h1 className="mt-5 font-display text-h1 leading-[1.03] tracking-tight text-ink md:text-display lg:text-display">
                {post.title}
              </h1>
              <p className="article-dek mt-5 max-w-2xl font-display text-h3 italic leading-snug text-ink/75 md:text-h2">
                {post.dek}
              </p>

              {post.updatedAt && post.updatedAt !== post.publishedAt && post.changelog ? (
                <aside
                  aria-label="Vad är nytt i den här uppdateringen"
                  className="mt-7 max-w-2xl overflow-hidden rounded-card border border-primary/30 bg-primary-soft/60 shadow-1"
                >
                  <div className="flex items-center justify-between gap-3 border-b border-primary/20 bg-primary/10 px-4 py-2.5">
                    <span className="mono inline-flex items-center gap-2 text-label text-primary">
                      <Sparkles className="h-3.5 w-3.5" aria-hidden />
                      Vad är nytt?
                    </span>
                    <span className="mono text-label text-ink-muted">
                      Uppdaterad {formatDate(post.updatedAt)}
                    </span>
                  </div>
                  <div className="px-4 py-3.5">
                    <p className="text-small leading-relaxed text-ink">{post.changelog.summary}</p>
                    {post.changelog.items && post.changelog.items.length > 0 ? (
                      <ul className="mt-2.5 space-y-1.5 text-small text-ink-muted">
                        {post.changelog.items.map((it, i) => (
                          <li key={i} className="flex gap-2">
                            <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-pill bg-primary" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </aside>
              ) : null}
            </div>

            <div className="lg:col-span-4">
              <div className="flex flex-col items-start gap-5 lg:items-end">
                <div className="flex items-center gap-3">
                  <div className="text-left lg:text-right">
                    <Link
                      to="/nyheter/skribent/$slug"
                      params={{ slug: resolveAuthor(post.author).slug }}
                      className="text-small font-semibold text-ink underline-offset-4 hover:text-primary hover:underline"
                    >
                      {post.author}
                    </Link>
                    <p className="mono text-label text-ink-muted">
                      {resolveAuthor(post.author).role}
                    </p>
                  </div>
                  <div
                    aria-hidden
                    className="grid h-12 w-12 shrink-0 place-items-center rounded-pill border-2 border-border bg-primary-soft font-display text-h4 text-primary"
                  >
                    {post.author.slice(0, 1)}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <ReadToggleButton slug={post.slug} />
                  <SaveButton slug={post.slug} />
                  <ResetProgressButton slug={post.slug} />
                  <ShareMenu
                    title={post.title}
                    text={post.dek}
                    url={shareUrl}
                    image={toAbsolute(origin ?? "", post.hero.src)}
                    imageAlt={post.heroAlt}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Cinematisk hero-bild */}
      <div className="mx-auto max-w-6xl px-5 pt-8 md:pt-10">
        <figure className="relative overflow-hidden rounded-card border border-border shadow-2">
          <ArticleImage
            src={post.hero}
            alt={post.heroAlt}
            aspect="21/9"
            priority
            sizes="(min-width: 1024px) 1024px, 100vw"
            overlaySkeleton={{ badge: false, caption: true }}
          >
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 via-ink/10 to-transparent" />
            <figcaption className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-3 p-5 md:p-7">
              <span className="max-w-xl font-display text-small italic text-surface/95 md:text-body">
                {post.heroAlt}
              </span>
              <span className="mono rounded-pill border border-surface/30 bg-ink/40 px-3 py-1 text-label text-surface/90 backdrop-blur">
                Foto · Skolvyn
              </span>
            </figcaption>
          </ArticleImage>
        </figure>
      </div>


      {/* Innehållslayout: 8/4 med brödtext + sidopanel */}
      <div className="mx-auto max-w-6xl px-5 py-section">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Brödtext */}
          <article id="article-body" className="lg:col-span-8">
            <MobileTableOfContents items={tocItems} />
            {bodyHead.map((para, i) => (

              <Reveal key={`h-${i}`} delay={i * 0.03}>
                <p
                  className={
                    i === 0
                      ? "font-display text-h2 leading-[1.5] text-ink first-letter:mr-2 first-letter:float-left first-letter:font-display first-letter:text-display first-letter:leading-[0.85] first-letter:text-primary first-letter:pr-1"
                      : "mt-7 font-display text-h3 leading-[1.7] text-ink md:text-h2"
                  }
                >
                  {para}
                </p>
              </Reveal>
            ))}

            {pullQuote ? (
              <Reveal>
                <figure className="my-10 rounded-card border-l-8 border-primary bg-surface p-card shadow-1 md:my-14 p-card">
                  <Quote
                    aria-hidden
                    className="mb-3 h-8 w-8 text-primary/70"
                  />
                  <blockquote className="font-display text-h2 italic leading-tight text-ink md:text-h1">
                    {pullQuote}
                  </blockquote>
                  <figcaption className="mono mt-5 text-label text-ink-muted">
                    — {post.author}, ur artikeln
                  </figcaption>
                </figure>
              </Reveal>
            ) : null}

            {bodyTail.map((para, i) => (
              <Reveal key={`t-${i}`} delay={i * 0.03}>
                <p className="mt-7 font-display text-h3 leading-[1.7] text-ink md:text-h2">
                  {para}
                </p>
              </Reveal>
            ))}

            {post.sections && post.sections.length > 0 && (
              <div className="mt-12 space-y-12">
                {post.sections.map((s, si) => (
                  <Reveal key={`sec-${si}`}>
                    <section aria-labelledby={tocItems[si]?.id}>
                      <h2
                        id={tocItems[si]?.id}
                        className="scroll-mt-24 font-display text-h1 leading-tight text-ink outline-none md:text-h1"
                      >
                        {s.heading}
                      </h2>

                      {s.paragraphs?.map((para, pi) => (
                        <p
                          key={`sec-${si}-p-${pi}`}
                          className="mt-5 font-display text-h4 leading-[1.7] text-ink md:text-h3"
                        >
                          {para}
                        </p>
                      ))}
                      {s.list && s.list.length > 0 && (
                        <ul className="mt-5 space-y-3">
                          {s.list.map((li, i) => (
                            <li
                              key={`sec-${si}-li-${i}`}
                              className="grid grid-cols-[1.5rem_1fr] gap-2 text-h4 leading-relaxed text-ink"
                            >
                              <span aria-hidden className="mono pt-1 text-primary">→</span>
                              <span>{li}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {s.table && (
                        <div className="mt-5">
                          <div
                            className="overflow-x-auto rounded-card border border-border"
                            tabIndex={0}
                            role="region"
                            aria-label={`Tabell: ${s.heading ?? "data"}`}
                          >
                            <table className="w-full min-w-[520px] text-left text-small">

                            <thead className="bg-surface-sunken">
                              <tr>
                                {s.table.headers.map((h, i) => (
                                  <th
                                    key={`th-${si}-${i}`}
                                    className="mono px-4 py-3 text-label text-ink-muted"
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {s.table.rows.map((row, ri) => (
                                <tr key={`tr-${si}-${ri}`} className="border-t border-border">
                                  {row.map((cell, ci) => (
                                    <td key={`td-${si}-${ri}-${ci}`} className="px-4 py-3 align-top text-ink">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                          <p className="mono mt-2 text-label text-ink-muted sm:hidden">
                            Dra i sidled för att se hela tabellen
                          </p>
                        </div>
                      )}

                    </section>
                  </Reveal>
                ))}
              </div>
            )}

            {post.internalLinks && post.internalLinks.length > 0 && (
              <Reveal>
                <aside
                  aria-label="Läs vidare inom Skolvyn"
                  className="mt-14 rounded-card border border-border bg-primary-soft/40 p-card"
                >
                  <div className="mono text-label text-primary">
                    Läs vidare
                  </div>
                  <ul className="mt-3 space-y-2">
                    {post.internalLinks.map((l, i) => (
                      <li key={`il-${i}`}>
                        <Link
                          to={l.to}
                          className="inline-flex items-baseline gap-2 font-display text-h4 text-ink underline decoration-primary/40 underline-offset-4 hover:text-primary hover:decoration-primary"
                        >
                          <ArrowUpRight aria-hidden className="h-4 w-4 shrink-0 text-primary" />
                          <span>{l.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </aside>
              </Reveal>
            )}

            <Reveal>
              <ArticleEngagement post={post} shareUrl={absoluteUrl(`/nyheter/${post.slug}`)} />
            </Reveal>



            {post.faq && post.faq.length > 0 && (
              <Reveal>
                <section
                  aria-labelledby="faq-rubrik"
                  className="mt-14 rounded-card border border-border bg-surface p-card shadow-1"
                >
                  <h2
                    id="faq-rubrik"
                    className="font-display text-h2 leading-tight text-ink md:text-h1"
                  >
                    Vanliga frågor
                  </h2>
                  <dl className="mt-5 divide-y divide-border">
                    {post.faq.map((f, i) => (
                      <div key={`faq-${i}`} className="py-4 first:pt-0 last:pb-0">
                        <dt className="font-display text-h4 font-semibold text-ink">
                          {f.q}
                        </dt>
                        <dd className="mt-2 text-body leading-relaxed text-ink/85">
                          {f.a}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </Reveal>
            )}


            {post.sources && post.sources.length > 0 && (
              <Reveal>
                <section
                  aria-labelledby="kallor-rubrik"
                  className="mt-14 rounded-card border border-border bg-surface-sunken/60 p-card"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen aria-hidden className="h-5 w-5 text-primary" />
                    <h2
                      id="kallor-rubrik"
                      className="font-display text-h2 leading-tight text-ink"
                    >
                      Källor och länkar
                    </h2>
                  </div>
                  <p className="mono mt-2 text-label text-ink-muted">
                    Varje påstående i artikeln ska gå att verifiera. Öppnas i ny flik.
                  </p>
                  <ol className="mt-5 space-y-4">
                    {post.sources.map((s, i) => (
                      <li
                        key={`${s.url}-${i}`}
                        className="grid grid-cols-[2rem_1fr] gap-3 border-t border-border pt-4 first:border-t-0 first:pt-0"
                      >
                        <span className="mono pt-0.5 text-small font-semibold text-primary">
                          [{i + 1}]
                        </span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="mono rounded-pill bg-primary-soft px-2 py-0.5 text-label text-primary">
                              {s.label}
                            </span>
                            {s.year && (
                              <span className="mono text-label text-ink-muted">
                                {s.year}
                              </span>
                            )}
                          </div>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="mt-1 inline-flex items-baseline gap-1 font-display text-h4 leading-snug text-ink underline decoration-primary/40 underline-offset-4 hover:text-primary hover:decoration-primary"
                          >
                            <span>{s.title}</span>
                            <ExternalLink
                              aria-hidden
                              className="h-3.5 w-3.5 shrink-0 translate-y-0.5 text-ink-muted"
                            />
                            <span className="sr-only"> (öppnas i ny flik)</span>
                          </a>
                          {s.note && (
                            <p className="mt-1 text-small text-ink-muted">{s.note}</p>
                          )}
                          <p className="mono mt-1 truncate text-small text-ink-muted/80">
                            {(() => {
                              try {
                                return new URL(s.url).hostname.replace(/^www\./, "");
                              } catch {
                                return s.url;
                              }
                            })()}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-6 text-small text-ink-muted">
                    Länkar hos externa parter kan ändras. Hittar du en trasig länk?{" "}
                    <a
                      href="mailto:redaktion@skolvyn.se?subject=Trasig%20k%C3%A4lla"
                      className="underline hover:text-primary"
                    >
                      Tipsa redaktionen
                    </a>
                    .
                  </p>
                </section>
              </Reveal>
            )}

            {post.tags.length > 0 && (
              <div
                className="mt-14 border-t border-border pt-6"
                aria-labelledby="tags-heading"
              >
                <h2 id="tags-heading" className="mono text-label text-ink-muted">
                  Taggar
                </h2>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {post.tags.map((t) => (
                    <li key={t}>
                      <Link
                        to="/nyheter"
                        search={{ tagg: t }}
                        className="inline-flex items-center rounded-pill border border-border bg-green-100 px-3 py-1 text-small text-ink/80 transition hover:-translate-y-0.5 hover:border-primary/40 hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30"
                        aria-label={`Visa alla inlägg med taggen ${t}`}
                      >
                        #{t}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}


            {(neighbors.prev || neighbors.next) && (
              <Reveal>
                <nav
                  aria-label="Föregående och nästa inlägg"
                  className="mt-14 grid gap-4 border-t border-border pt-8 sm:grid-cols-2"
                >
                  {neighbors.prev ? (
                    <Link
                      to="/nyheter/$slug"
                      params={{ slug: neighbors.prev.slug }}
                      className="group flex flex-col rounded-card border border-border bg-surface p-card transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-2 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30"
                    >
                      <span className="mono inline-flex items-center gap-2 text-label text-ink-muted">
                        <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
                        Föregående inlägg
                      </span>
                      <span className="mt-2 font-display text-h4 leading-snug text-ink group-hover:text-primary">
                        {neighbors.prev.title}
                      </span>
                      <span className="mono mt-2 text-label text-ink-muted">
                        {CATEGORY_LABELS[neighbors.prev.category]}
                      </span>
                    </Link>
                  ) : (
                    <span aria-hidden />
                  )}
                  {neighbors.next ? (
                    <Link
                      to="/nyheter/$slug"
                      params={{ slug: neighbors.next.slug }}
                      className="group flex flex-col rounded-card border border-border bg-surface p-card text-right transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-2 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30 sm:items-end"
                    >
                      <span className="mono inline-flex items-center gap-2 text-label text-ink-muted">
                        Nästa inlägg
                        <ArrowRight aria-hidden className="h-3.5 w-3.5" />
                      </span>
                      <span className="mt-2 font-display text-h4 leading-snug text-ink group-hover:text-primary">
                        {neighbors.next.title}
                      </span>
                      <span className="mono mt-2 text-label text-ink-muted">
                        {CATEGORY_LABELS[neighbors.next.category]}
                      </span>
                    </Link>
                  ) : (
                    <span aria-hidden />
                  )}
                </nav>
              </Reveal>
            )}
          </article>

          {/* Sidopanel: innehåll + relaterat */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-10">
              <TableOfContents items={tocItems} />

              <div className="relative overflow-hidden rounded-card bg-primary p-card text-primary-foreground shadow-2">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-pill bg-primary-foreground/10 blur-2xl"
                />
                <Rss aria-hidden className="mb-4 h-6 w-6 text-primary-foreground/80" />
                <h3 className="font-display text-h2 leading-snug">
                  Håll dig uppdaterad
                </h3>
                <p className="mt-2 text-small text-primary-foreground/75">
                  Analyser, guider och nya inlägg – följ flödet i din läsare.
                </p>
                <a
                  href="/nyheter/rss.xml"
                  className="mono mt-5 inline-flex items-center gap-2 rounded-card bg-primary-foreground px-4 py-3 text-small font-semibold uppercase tracking-widest text-primary transition hover:-translate-y-0.5 hover:shadow-2"
                >
                  <Rss className="h-3.5 w-3.5" aria-hidden /> Följ via RSS
                </a>
              </div>


              {related.length > 0 && (
                <div>
                  <h3 className="mono border-b border-border pb-3 text-label text-ink-muted">
                    Läs vidare
                  </h3>
                  <ul className="mt-4 space-y-6">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link
                          to="/nyheter/$slug"
                          params={{ slug: r.slug }}
                          className="group block"
                        >
                          <ArticleImage
                            src={r.hero}
                            alt={r.heroAlt}
                            aspect="16/9"
                            className="rounded-card border border-border"
                            imgClassName="transition duration-700 group-hover:scale-105"
                            sizes="(min-width: 1024px) 320px, 100vw"
                            overlaySkeleton={{ badge: { width: 80 }, caption: false }}
                            fallbackLabel="Relaterad artikel"
                          />

                          <p className="mono mt-3 text-label text-ink-muted">
                            {CATEGORY_LABELS[r.category]}
                          </p>
                          <h4 className="mt-1 font-display text-h4 leading-snug text-ink group-hover:text-primary">
                            {r.title}
                          </h4>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Prefetch-sentinel: triggar hero-bilds- och route-prefetch när
          användaren närmar sig "Läs vidare"-bandet (rootMargin 800px). */}
      <div
        ref={relatedSentinelRef}
        aria-hidden="true"
        className="pointer-events-none h-px w-full"
      />

      {/* Bredare "Läs vidare"-band */}
      {related.length > 0 && (
        <section
          className="warmth-bg border-t border-border"
          aria-labelledby="related-heading"
        >
          <div className="mx-auto max-w-7xl px-5 py-section">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-2xl">
                <span className="mono inline-flex items-center gap-2 rounded-pill border border-border bg-surface/70 px-3 py-1 text-label text-ink-muted">
                  <BookOpen className="h-3.5 w-3.5" /> Läs vidare
                </span>
                <h2
                  id="related-heading"
                  className="mt-3 font-display text-h1 leading-tight text-ink md:text-h1"
                >
                  Fortsätt tråden – handplockat att läsa efter den här artikeln
                </h2>
                <p className="mt-2 font-display text-h4 italic text-ink-muted">
                  Fyra inlägg som fördjupar samma fråga eller ger perspektiv från en annan vinkel.
                </p>
              </div>
              <Link
                to="/nyheter"
                className="mono inline-flex items-center gap-1.5 rounded-pill border border-border bg-surface px-4 py-2 text-small uppercase tracking-widest text-ink shadow-1 transition hover:border-primary hover:text-primary"
              >
                Alla inlägg <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <ul className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {related.map((r, idx) => (
                <li key={r.slug}>
                  <Reveal delay={idx * 60}>
                    <Link
                      to="/nyheter/$slug"
                      params={{ slug: r.slug }}
                      className="group flex h-full flex-col overflow-hidden rounded-card border border-border bg-surface shadow-1 transition hover:-translate-y-0.5 hover:shadow-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                    >
                      <ArticleImage
                        src={r.hero}
                        alt={r.heroAlt}
                        aspect="16/10"
                        imgClassName="transition duration-700 group-hover:scale-105"
                        sizes="(min-width: 1280px) 320px, (min-width: 768px) 50vw, 100vw"
                        overlaySkeleton={{ badge: { width: 96 }, caption: false }}
                        fallbackLabel="Läs vidare"
                      >
                        <span
                          className={`mono absolute left-3 top-3 rounded-pill px-2.5 py-0.5 text-label shadow-1 ${CATEGORY_ACCENT[r.category]}`}
                        >
                          {CATEGORY_LABELS[r.category]}
                        </span>
                      </ArticleImage>

                      <div className="flex flex-1 flex-col gap-3 p-5">
                        <h3 className="font-display text-h3 leading-snug text-ink group-hover:text-primary">
                          {r.title}
                        </h3>
                        <p className="line-clamp-3 text-small text-ink-muted">{r.dek}</p>
                        <div className="mono mt-auto flex flex-wrap items-center gap-3 pt-2 text-small text-ink-muted">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {readingMinutes(r)} min
                          </span>
                          <FreshnessBadge post={r} compact className="ml-auto" />
                        </div>
                        <span className="inline-flex items-center gap-1 pt-1 text-small font-medium text-primary">
                          Läs inlägget <ArrowUpRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-card border border-border bg-surface/70 px-5 py-4 backdrop-blur">
              <p className="font-display text-body italic text-ink">
                Vill du inte missa nästa fördjupning? Vi skickar en kort veckoblick – inget brus.
              </p>
              <div className="flex items-center gap-2">
                <Link
                  to="/nyheter/sparade"
                  search={{ q: "", sort: "saved-desc", kategori: "" }}
                  className="mono inline-flex items-center gap-1.5 rounded-pill border border-border bg-surface px-4 py-2 text-small uppercase tracking-widest text-ink transition hover:border-primary hover:text-primary"
                >
                  <BookOpen className="h-3.5 w-3.5" /> Min läslista
                </Link>
                <a
                  href="/nyheter/rss.xml"
                  className="mono inline-flex items-center gap-1.5 rounded-pill bg-primary px-4 py-2 text-small uppercase tracking-widest text-primary-foreground shadow-1 transition hover:bg-primary/90"
                >
                  <Rss className="h-3.5 w-3.5" /> Prenumerera
                </a>
              </div>
            </div>

            <p className="mono mt-6 text-center text-small text-ink-muted">
              {totalPosts} inlägg totalt · uppdateras löpande av redaktionen
            </p>
          </div>
        </section>
      )}

      <NewsFeedFooter />
    </div>
  );
}
